import { Bunja } from 'bunja';
import { useBunja } from 'bunja/react';

import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';

import { devtoolsRouteBunja } from '../../../state';
import { GraphRow, buildGraphRows } from './dag-visualizer';

interface BunjaDependencyGraphProps {
  bunja: Bunja<unknown>;
}

// for bake panda css colors
css({ color: 'blue.600' });
css({ color: 'gray.500' });
css({ color: 'red.600' });
css({ color: 'yellow.600' });
css({ color: 'green.600' });
css({ color: 'indigo.600' });
const columnColors = ['blue.600', 'gray.500', 'red.600', 'yellow.600', 'green.600', 'indigo.600'];

export const BunjaDependencyGraph = ({ bunja }: BunjaDependencyGraphProps) => {
  const parents = bunja.relatedBunjas || [];
  if (parents.length === 0) return null;
  const rows = buildGraphRows(bunja);
  return (
    <div className={css({ px: 8 })}>
      {rows.map((row, rowIndex) => {
        const isNodeRow = row.type === 'node';
        const nodeRowIndex = rows.slice(0, rowIndex + 1).filter((r) => r.type === 'node').length - 1;
        return (
          <div key={`row-${rowIndex}`} className={flex({ gap: 2, alignItems: 'center', h: 14 })}>
            <GraphVisualization row={row} />
            {isNodeRow && <NodeInfo row={row} nodeRowIndex={nodeRowIndex} />}
          </div>
        );
      })}
    </div>
  );
};

interface GraphVisualizationProps {
  row: GraphRow;
}
const GraphVisualization = ({ row }: GraphVisualizationProps) => {
  return (
    <span
      className={css({
        textStyle: 'Text/S/Monospace/Regular',
        fontFamily: 'monospace',
        whiteSpace: 'pre',
        display: 'inline-flex',
      })}
    >
      {row.segments.map((seg, segIdx) => (
        <span key={segIdx} className={css({ color: columnColors[seg.color] })}>
          {seg.char}
        </span>
      ))}
    </span>
  );
};

interface NodeInfoProps {
  row: GraphRow;
  nodeRowIndex: number;
}
const NodeInfo = ({ row, nodeRowIndex }: NodeInfoProps) => {
  const { changeTab } = useBunja(devtoolsRouteBunja);
  if (!row.node) return null;
  const handleClick = () => changeTab('dag', { selectedBunjaId: row.node!.id });
  return (
    <button
      onClick={handleClick}
      className={flex({
        gap: 4,
        alignItems: 'center',
        cursor: 'pointer',
        border: 'none',
        bgColor: 'transparent',
        px: 4,
        py: 2,
        mx: -4,
        my: -2,
        rounded: 4,
        _hover: { bgColor: 'background.default.level-3' },
      })}
    >
      <span
        className={css({
          textStyle: 'Text/S/Monospace/Regular',
          color: nodeRowIndex === 0 ? 'content.accent.info' : 'inverse.white',
          fontWeight: nodeRowIndex === 0 ? 'bold' : 'normal',
        })}
      >
        {row.node.debugLabel || 'Anonymous'}
      </span>
      <span
        className={css({
          textStyle: 'Text/XS/Monospace/Regular',
          color: 'content.default.level-3',
        })}
      >
        #{row.node.id}
      </span>
    </button>
  );
};
